import {
  CheckboxField,
  Flex,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@aws-amplify/ui-react"
import type { TrashedFeedingReport } from "@strtmn/sds/types"
import { getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useCallback, useEffect, useState } from "react"
import { FaTrashCanArrowUp, FaXmark } from "react-icons/fa6"
import { useParams } from "react-router-dom"
import {
  listFeedingReportsBin,
  restoreFeedingReports,
} from "../../../api/FeedingReportsApi"
import { LoadingErrorState } from "../../../components"
import { ButtonWithIcon } from "../../../components/ButtonWithIcon"

const daysRemaining = (restoreDeadline: string): number =>
  Math.max(
    0,
    Math.ceil(
      (new Date(restoreDeadline).getTime() - Date.now()) / (24 * 60 * 60 * 1000)
    )
  )

export default function Bin() {
  const params = useParams<{ orgId: string }>()
  const orgId = params.orgId!

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const [items, setItems] = useState<TrashedFeedingReport[]>([])
  const [selectedIds, setSelectedIds] = useState(new Set<string>())
  const [restoreError, setRestoreError] = useState<string | undefined>(
    undefined
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(undefined)
    try {
      const data = await listFeedingReportsBin(orgId)
      setItems(data)
    } catch (err) {
      console.error(err)
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    load()
  }, [load])

  const allSelected = items.length > 0 && selectedIds.size === items.length
  const someSelected = selectedIds.size > 0 && !allSelected

  const handleHeaderCheckbox = () => {
    if (allSelected) {
      setSelectedIds(new Set<string>())
    } else {
      setSelectedIds(new Set(items.map((item) => item.feedingReportId)))
    }
  }

  const handleRowCheckbox = (feedingReportId: string) => {
    setSelectedIds((prev) => {
      const next = new Set<string>(prev)
      next.has(feedingReportId)
        ? next.delete(feedingReportId)
        : next.add(feedingReportId)
      return next
    })
  }

  const handleRestore = async (feedingReportIds: string[]) => {
    const results = await restoreFeedingReports(orgId, feedingReportIds)
    // "not-found" (window expired / already restored elsewhere) is treated
    // like success: nothing more to do for that id. Only "failed" stays
    // selected, so the user can see what didn't go through and retry.
    const succeededIds = new Set(
      results
        .filter((r) => r.status === "restored" || r.status === "not-found")
        .map((r) => r.feedingReportId)
    )
    const failedCount = results.length - succeededIds.size

    setSelectedIds((prev) => {
      const next = new Set(prev)
      succeededIds.forEach((id) => next.delete(id))
      return next
    })
    setRestoreError(
      failedCount > 0
        ? `${failedCount} von ${results.length} Fütterungen konnten nicht wiederhergestellt werden. Bitte versuchen Sie es erneut.`
        : undefined
    )
    if (succeededIds.size > 0) {
      await load()
    }
  }

  return (
    <div className="rm-list-page">
      <Flex className="!h-12 !pb-2 !justify-between !items-center">
        <Flex className="!h-6 !justify-start !items-center gap-3">
          <FaTrashCanArrowUp className="w-5 relativ text-[#3E9E29]" />
          <div className="text-xl font-semibold">{I18n.get("Papierkorb")}</div>
        </Flex>
      </Flex>

      {loading && (
        <>
          <LoadingErrorState
            isLoading={loading}
            loadingMessage={I18n.get("Lade Daten...")}
            isError={false}
          />
          <Loader
            filledColor="#498e39"
            variation="linear"
            className="!max-w-xl"
          />
        </>
      )}

      {!loading && error && (
        <LoadingErrorState isError errorMessage={error} isLoading={false} />
      )}

      {restoreError && (
        <div className="export-banner export-banner-error">
          <span>✗ {restoreError}</span>
          <button
            onClick={() => setRestoreError(undefined)}
            className="export-banner-close"
            aria-label={I18n.get("Schließen")}
          >
            <FaXmark className="inline" />
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="my-4 text-gray-500">
          {I18n.get("Der Papierkorb ist leer.")}
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="rm-list-container">
          <ButtonWithIcon
            title={`${I18n.get("Wiederherstellen")} (${selectedIds.size})`}
            icon={<FaTrashCanArrowUp />}
            onClick={() => handleRestore([...selectedIds])}
            isDisabled={selectedIds.size === 0}
          />

          <div className="overflow-x-auto">
            <Table highlightOnHover={true} size="small" variation="striped">
              <TableHead>
                <TableRow className="bg-gray-300">
                  <TableCell
                    as="th"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleHeaderCheckbox()
                    }}
                    className="cursor-pointer"
                  >
                    <CheckboxField
                      label=""
                      labelHidden
                      name="select-all"
                      value="all"
                      checked={allSelected}
                      isIndeterminate={someSelected}
                      readOnly
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell as="th">{I18n.get("Fütterung vom")}</TableCell>
                  <TableCell as="th">{I18n.get("Rezeptname")}</TableCell>
                  <TableCell as="th">{I18n.get("Fahrer")}</TableCell>
                  <TableCell as="th">{I18n.get("Gelöscht am")}</TableCell>
                  <TableCell as="th">{I18n.get("Verbleibende Tage")}</TableCell>
                  <TableCell as="th" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.feedingReportId}>
                    <TableCell
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRowCheckbox(item.feedingReportId)
                      }}
                      className="cursor-pointer"
                    >
                      <CheckboxField
                        label=""
                        labelHidden
                        name={`select-${item.feedingReportId}`}
                        value={item.feedingReportId}
                        checked={selectedIds.has(item.feedingReportId)}
                        readOnly
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(item.reportDate).toLocaleString("de-de", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </TableCell>
                    <TableCell>{item.recipeName}</TableCell>
                    <TableCell>{item.driver}</TableCell>
                    <TableCell>
                      {new Date(item.deletedAt).toLocaleString("de-de", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </TableCell>
                    <TableCell>{daysRemaining(item.restoreDeadline)}</TableCell>
                    <TableCell>
                      <ButtonWithIcon
                        title={I18n.get("Wiederherstellen")}
                        icon={<FaTrashCanArrowUp />}
                        onClick={() => handleRestore([item.feedingReportId])}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}
